import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {FONT_PROXIMA_12, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_DARKER, BLUE_LIGHTEST, GRAY_LIGHT, GRAY_LIGHTER, GRAY_SEMIDARK} from 'ui-library/Colors';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {OnboardingUnit, OnboardingUnitType} from 'units/UnitDictionaryDAO';

import AddToUnitDictionaryButton from './AddToUnitDictionaryButton';

export interface UnitsOnboardingWizardUnitTypeRowProps {
  unitType: OnboardingUnitType;
  added: boolean;
  unitTypeUnits: Array<OnboardingUnit>;
  onSetAdded: (selected: boolean) => void;
  forceSelectedText?: string | null;
  onSetCurrentUnitTypeName: (currentUnitTypeName: string | null) => void;
  selectedUnitSymbols: Array<string>;
}

const INVERTED_DUOTONE_COLOR_UNIT_TYPES = ['Volume', 'Molar concentration', 'Thermodynamic temperature'];

export default function UnitsOnboardingWizardUnitTypeRow({
  unitType,
  added,
  unitTypeUnits,
  onSetAdded,
  forceSelectedText,
  onSetCurrentUnitTypeName,
  selectedUnitSymbols,
}: UnitsOnboardingWizardUnitTypeRowProps): JSX.Element {
  const selectedUnitSymbolsSet = new Set(selectedUnitSymbols);
  const numUnitsSelected = unitTypeUnits.filter((unit) => selectedUnitSymbolsSet.has(unit.symbol)).length;
  const numUnitsDetailText = `${numUnitsSelected} of ${unitTypeUnits.length} ${pluralize(
    unitTypeUnits.length,
    'unit'
  )} selected`;
  const iconPrimaryColor = INVERTED_DUOTONE_COLOR_UNIT_TYPES.includes(unitType.name)
    ? BLUE_LIGHTEST
    : BLUE_DARKER;
  const iconSecondaryColor = INVERTED_DUOTONE_COLOR_UNIT_TYPES.includes(unitType.name)
    ? BLUE_DARKER
    : BLUE_LIGHTEST;
  return (
    <div
      className={css(styles.rowContainer)}
      onClick={() => {
        onSetCurrentUnitTypeName(unitType.name);
      }}
      {...getTestProps({component: 'UnitsOnboardingWizardUnitTypeRow', element: 'root'})}
    >
      <div className={css(styles.leftPortion)}>
        {unitType?.icon != null && (
          <div className={css(styles.iconContainer)}>
            <BenchlingDuotoneIcon
              icon={unitType.icon}
              primaryColor={iconPrimaryColor}
              secondaryColor={iconSecondaryColor}
            />
          </div>
        )}
        <div className={css(styles.textContainer)}>
          <span className={css(styles.unitTypeName)}>{unitType.name}</span>
          <span className={css(styles.numUnitsDetailText)}>{numUnitsDetailText}</span>
        </div>
      </div>
      <div className={css(styles.rightPortion)}>
        <AddToUnitDictionaryButton
          added={added}
          onSetAdded={onSetAdded}
          forceSelectedText={forceSelectedText}
        />
        <BenchlingIcon icon="chevron-right" className={css(styles.chevron)} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  rowContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: 65,
    borderTop: `1px solid ${GRAY_LIGHT}`,
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 12,
  },
  unitTypeName: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  numUnitsDetailText: {
    ...FONT_PROXIMA_12,
    color: GRAY_SEMIDARK,
  },
  iconContainer: {
    width: 64,
    height: 48,
    borderRadius: 4,
    padding: '8px 16px',
    backgroundColor: GRAY_LIGHTER,
    fontSize: 32,
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
  },
  icon: {
    width: 32,
    height: 32,
  },
  chevron: {
    width: 16,
    height: 16,
    marginLeft: 12,
    marginRight: 12,
  },
  leftPortion: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 7,
  },
  rightPortion: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 7,
  },
});
